// **React Imports
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

// **MUI Imports
import { Box, Button, InputAdornment, Typography } from '@mui/material'

// **Component Imports
import CustomTextfield from '../../components/textfield'
import ControlledTextField from '../controlled-textfield'
import CustomDialog from '../dialog'
import Icon from '../icon'
import OverlayLoading from '../overlay-loading'

// **Third party Imports
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import * as yup from 'yup'

// **Redux Imports
import { useDispatch, useSelector } from 'react-redux'
import {
  setHasInitialSetup,
  setProfileDetails,
  setTenantList
} from '../../store/slice/auth'

// **Utils Imports
import { ROLES } from '../../utils/constant'
import { checkUserAccess, showErrorToast } from '../../utils/utils'
import {
  addressValidation,
  validateRequiredString
} from '../../utils/validations'

// **API Imports
import { fetchCountries } from '../../axios/api/address'
import { checkNewUser, updateTenantDetails } from '../../axios/api/auth'
import { getProfileDetails } from '../../axios/api/profile'
import FieldsetContainer from '../../pages/contest/edit/components/FieldsetContainer'
import VirtualizedAutocomplete from '../virtualized-autocomplete'

// Constants
const defaultValue = {
  name: '',
  tenantName: '',
  gstinNumber: '',
  address: {
    companyName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    zipcode: ''
  }
}

// Schema
const yupSchema = yup.object().shape({
  name: validateRequiredString('Full name'),
  tenantName: validateRequiredString('Company name'),
  address: addressValidation
})

const TenantDetailsModal = () => {
  // hooks
  const dispatch = useDispatch()

  // redux values
  const isLoggedIn = useSelector((state) => state.auth.token)
  const tenantList = useSelector((state) => state.auth.tenantList)
  const userData = useSelector((state) => state.auth.userData)

  // states
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [countries, setCountries] = useState([])

  // form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    defaultValue: defaultValue,
    mode: 'onChange',
    resolver: yupResolver(yupSchema)
  })

  const countyWatch = watch('address.country')
  // handlers
  const updateTenantList = useCallback(
    (value) => {
      if (tenantList?.length > 0) {
        const updatedList = tenantList.map((item) =>
          item?.id === userData?.tenantId && item?.name !== value
            ? { ...item, name: value }
            : item
        )
        dispatch(setTenantList(updatedList))
      }
    },
    [tenantList, userData?.tenantId, dispatch]
  )

  const isIndianCountry = useMemo(() => {
    return countyWatch?.toLowerCase() === 'india'
  }, [countyWatch])

  const fetchProfile = useCallback(async () => {
    setLoading(true)
    const response = await getProfileDetails()
    setLoading(false)

    if (response?.isError) {
      showErrorToast(response)
    } else {
      dispatch(setProfileDetails(response))
      updateTenantList(response?.tenantName || '')
    }
  }, [dispatch, updateTenantList])

  const updateDetails = useCallback(
    async (payload) => {
      setLoading(true)
      const response = await updateTenantDetails(payload)
      setLoading(false)

      if (response?.isError) {
        showErrorToast(response)
        dispatch(setHasInitialSetup(false))
      } else {
        fetchProfile()
        setOpen(false)
        toast.success('Profile updated successfully')
        dispatch(setHasInitialSetup(true))
      }
    },
    [fetchProfile, dispatch]
  )

  const fetchFirstLoginFlag = useCallback(async () => {
    setLoading(true)
    const response = await checkNewUser()
    setLoading(false)

    if (response?.isError) {
      showErrorToast(response)
    } else {
      setOpen(response?.isNewUser)
      if (!response?.isNewUser) {
        dispatch(setHasInitialSetup(true))
      }
    }
  }, [dispatch])

  const onSubmit = useCallback(
    async (data) => {
      if (data) {
        await updateDetails(data)
      }
    },
    [updateDetails]
  )

  const fetchCountry = useCallback(async () => {
    setLoading(true)
    const res = await fetchCountries()
    if (res?.isError) {
      showErrorToast(res)
    } else {
      setCountries(res)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    if (checkUserAccess([ROLES.ADMIN]) && isLoggedIn) {
      fetchFirstLoginFlag()
    }
  }, [isLoggedIn, fetchFirstLoginFlag])

  useEffect(() => {
    if (open) {
      fetchCountry()
    }
  }, [open, fetchCountry])

  return (
    <Box>
      {loading && <OverlayLoading />}

      {open && (
        <CustomDialog
          outsideClikClose={false}
          open={open}
          dialogPaperStyle={{
            width: '80%',
            maxWidth: '1000px'
          }}
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="tenant-details-modal-container"
          >
            <Typography variant="h3" sx={{ mb: 1 }}>
              Complete Your Profile
            </Typography>
            <Typography sx={{ color: 'text.secondary', mb: 8 }}>
              Just a few quick details to get you started. 🚀
            </Typography>

            <FieldsetContainer label={'Company & User Name'}>
              <Box mb={2} display={'flex'} gap={'12px'}>
                <ControlledTextField
                  autoFocus
                  fullWidth
                  required
                  name={'name'}
                  control={control}
                  label={'Full name'}
                  error={Boolean(errors.name)}
                  {...(errors.name && {
                    helperText: errors.name.message
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Icon icon={'mdi:person'} />
                      </InputAdornment>
                    )
                  }}
                />

                <ControlledTextField
                  fullWidth
                  name={'tenantName'}
                  control={control}
                  label={'Company Name'}
                  required
                  error={Boolean(errors.tenantName)}
                  {...(errors.tenantName && {
                    helperText: errors.tenantName.message
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Icon icon={'mdi:business'} />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
            </FieldsetContainer>

            <FieldsetContainer
              label={'Billing Information'}
              className="address-information"
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: '12px',
                  mb: 2
                }}
              >
                <ControlledTextField
                  control={control}
                  name="address.companyName"
                  type="text"
                  fullWidth
                  label="Billing Company Name"
                  error={Boolean(errors.address?.companyName)}
                  {...(errors.address?.companyName && {
                    helperText: errors.address.companyName.message
                  })}
                />
                <ControlledTextField
                  control={control}
                  name="address.addressLine1"
                  type="text"
                  fullWidth
                  label="Address Line 1"
                  error={Boolean(errors.address?.addressLine1)}
                  {...(errors.address?.addressLine1 && {
                    helperText: errors.address.addressLine1.message
                  })}
                />
                <ControlledTextField
                  control={control}
                  name="address.addressLine2"
                  type="text"
                  fullWidth
                  label="Address Line 2"
                  error={Boolean(errors.address?.addressLine2)}
                  {...(errors.address?.addressLine2 && {
                    helperText: errors.address.addressLine2.message
                  })}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: '12px',
                  mb: 2,
                  '& > *': {
                    flex: 1,
                    width: '50%'
                  }
                }}
              >
                <ControlledTextField
                  control={control}
                  name="address.city"
                  type="text"
                  fullWidth
                  label="City"
                  error={Boolean(errors.address?.city)}
                  {...(errors.address?.city && {
                    helperText: errors.address.city.message
                  })}
                />
                <ControlledTextField
                  control={control}
                  name="address.zipcode"
                  type="text"
                  fullWidth
                  error={Boolean(errors?.address?.zipcode)}
                  label="ZIP/Postal Code"
                  {...(errors?.address?.zipcode && {
                    helperText: errors?.address?.zipcode.message
                  })}
                />
                <ControlledTextField
                  control={control}
                  name="address.state"
                  required={isIndianCountry}
                  type="text"
                  label="State"
                  fullWidth
                  error={Boolean(errors?.address?.state)}
                  {...(errors?.address?.state && {
                    helperText: errors.address.state.message
                  })}
                />
                <VirtualizedAutocomplete
                  control={control}
                  required={true}
                  needVirtualization={false}
                  options={countries}
                  name="address.country"
                  type="text"
                  fullWidth
                  label="Country"
                  error={Boolean(errors?.address?.country)}
                  {...(errors?.address?.country && {
                    helperText: errors.address.country.message
                  })}
                  autoCompleteProps={{
                    renderInput: (param) => {
                      return <CustomTextfield fullWidth {...param} />
                    }
                  }}
                />
              </Box>
            </FieldsetContainer>

            {isIndianCountry && (
              <FieldsetContainer label={'Company Tax Information'}>
                <Box mb={2} display={'flex'} gap={'12px'} width={'50%'}>
                  <ControlledTextField
                    fullWidth
                    name={'gstinNumber'}
                    control={control}
                    label={'GSTIN Number'}
                  />
                </Box>
              </FieldsetContainer>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end'
              }}
            >
              <Button variant="contained" color="primary" type={'submit'}>
                Save
              </Button>
            </Box>
          </form>
        </CustomDialog>
      )}
    </Box>
  )
}

export default memo(TenantDetailsModal)
